import {
  type DateRange,
  DayPicker,
  dateMatchModifiers,
  type Matcher,
  type Modifiers,
} from "@daypicker/react";
import {addDays, subDays} from "date-fns";
import {Calendar as CalendarIcon} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {type MatterLocale, useMatterLocale} from "@/components/contexts/MatterJurisdictionContext";
import {Chevron, dayPickerBaseClassNames, monthStart, TopControls} from "@/components/ui/DatePickerRoot";
import {useDialogContainer} from "@/components/ui/dialog-container-context";
import {Button as NewButton} from "@/components/ui/NewButton";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {formatRegionalDate} from "@/utils/date-utils";

const sameDate = (left?: Date, right?: Date) => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.getTime() === right.getTime();
};

const sameRange = (left?: DateRange, right?: DateRange) => {
  return sameDate(left?.from, right?.from) && sameDate(left?.to, right?.to);
};

const isCompleteRange = (value?: DateRange): value is DateRange & { from: Date; to: Date } => {
  return Boolean(value?.from && value?.to);
};

export const presetConfig = {
  today: {label: "Today", fromOffset: 0, toOffset: 0},
  yesterday: {label: "Yesterday", fromOffset: -1, toOffset: -1},
  last7Days: {label: "Last 7 days", fromOffset: -7, toOffset: 0},
  last30Days: {label: "Last 30 days", fromOffset: -30, toOffset: 0},
  last90Days: {label: "Last 90 days", fromOffset: -90, toOffset: 0},
  next7Days: {label: "Next 7 days", fromOffset: 0, toOffset: 7},
  next30Days: {label: "Next 30 days", fromOffset: 0, toOffset: 30},
  next90Days: {label: "Next 90 days", fromOffset: 0, toOffset: 90},
} as const;

export type DateRangePickerPreset = keyof typeof presetConfig;

/**
 * A caller-defined preset with an explicit (absolute) range. Use for dynamic
 * presets that can't be expressed as relative offsets in {@link presetConfig},
 * e.g. "jump to a specific year" derived from the data on screen.
 */
export type DateRangePreset = {
  id: string;
  label: string;
  range: DateRange;
};

type DateRangeCalendarProps = {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  onDayClick?: (day: Date, modifiers: Modifiers) => void;
  disabledDates?: Matcher | Matcher[];
  footerPresets?: DateRangePickerPreset[];
  customPresets?: DateRangePreset[];
  showClearSelection?: boolean;
  showRangeSummary?: boolean;
  showTopControls?: boolean;
  initialMonth?: Date;
  onPresetSelect?: (preset: DateRangePickerPreset) => void;
  onClearSelection?: () => void;
};

const getClampedPresetRange = (
  preset: DateRangePickerPreset,
  disabledDates?: Matcher | Matcher[],
): DateRange | undefined => {
  const today = new Date();
  const {fromOffset, toOffset} = presetConfig[preset];
  let from = addDays(today, fromOffset);
  let to = addDays(today, toOffset);

  while (
    disabledDates &&
    dateMatchModifiers(from, disabledDates) &&
    from.getTime() !== to.getTime()
  ) {
    from = addDays(from, from < to ? 1 : -1);
  }

  while (
    disabledDates &&
    dateMatchModifiers(to, disabledDates) &&
    from.getTime() !== to.getTime()
  ) {
    to = addDays(to, to > from ? -1 : 1);
  }

  if (
    (disabledDates && dateMatchModifiers(from, disabledDates)) ||
    (disabledDates && dateMatchModifiers(to, disabledDates))
  ) {
    return undefined;
  }

  return {from, to};
};

const getDefaultDateRange = (disabledDates?: Matcher | Matcher[]) => {
  return getClampedPresetRange("last7Days", disabledDates) ?? {
    from: subDays(new Date(), 7),
    to: new Date(),
  };
};

const getResolvedRange = (value: DateRange | undefined, disabledDates?: Matcher | Matcher[]) => {
  return isCompleteRange(value) ? value : getDefaultDateRange(disabledDates);
};

const formatRange = (value: DateRange, locale: MatterLocale) => {
  const {from, to} = value;

  if (!from) {
    return "Pick a date range";
  }

  if (!to) {
    return formatRegionalDate(from, locale);
  }

  return `${formatRegionalDate(from, locale)} - ${formatRegionalDate(to, locale)}`;
};

export const DateRangeCalendar = ({
  value,
  onChange,
  onDayClick,
  disabledDates,
  footerPresets,
  customPresets,
  showClearSelection = false,
  showRangeSummary = false,
  showTopControls = false,
  initialMonth,
  onPresetSelect,
  onClearSelection,
}: DateRangeCalendarProps) => {
  const locale = useMatterLocale();
  const resolvedInitialMonth = monthStart(initialMonth ?? value?.from ?? new Date());
  const resolvedInitialMonthTime = resolvedInitialMonth.getTime();
  const [month, setMonth] = useState(() => resolvedInitialMonth);

  useEffect(() => {
    setMonth(new Date(resolvedInitialMonthTime));
  }, [resolvedInitialMonthTime]);

  const handlePresetSelect = (preset: DateRangePickerPreset) => {
    if (onPresetSelect) {
      onPresetSelect(preset);
      return;
    }

    onChange?.(getClampedPresetRange(preset, disabledDates));
  };

  const handleClearSelection = () => {
    if (onClearSelection) {
      onClearSelection();
      return;
    }

    onChange?.(undefined);
  };

  const handleCustomPresetSelect = (preset: DateRangePreset) => {
    // Toggle off when the current selection already matches the preset.
    if (sameRange(value, preset.range)) {
      handleClearSelection();
      return;
    }

    onChange?.(preset.range);

    if (preset.range.from) {
      setMonth(monthStart(preset.range.from));
    }
  };

  const hasFooter = Boolean(
    footerPresets?.length || customPresets?.length || (showClearSelection && value),
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm">
      {showRangeSummary && (
        <div
          className={cn(
            "flex w-full flex-row items-center justify-center gap-2 border px-4 py-2 text-sm font-medium",
            value ? "text-gray-900" : "text-muted-foreground",
          )}
        >
          <CalendarIcon className="h-4 w-4"/>
          {value?.from ? formatRange(value, locale) : <span>Pick a date range</span>}
        </div>
      )}
      <TopControls enabled={showTopControls} onChange={setMonth} month={month}/>
      <DayPicker
        mode="range"
        month={month}
        onMonthChange={setMonth}
        selected={value}
        resetOnSelect
        onSelect={onChange}
        onDayClick={onDayClick}
        disabled={disabledDates}
        numberOfMonths={2}
        className="p-3"
        classNames={{
          ...dayPickerBaseClassNames,
          day:
            "relative h-8 w-8 flex-none p-0 text-center text-sm focus-within:relative focus-within:z-20 aria-selected:bg-accent [&.day-outside]:aria-selected:bg-accent/50 [&.day-range-end]:rounded-r-md [&.day-range-start]:rounded-l-md first:aria-selected:rounded-l-md last:aria-selected:rounded-r-md",
          day_button:
            "inline-flex h-8 w-8 items-center justify-center rounded-none p-0 font-normal text-gray-900 aria-selected:opacity-100",
          selected:
            "[&>button]:rounded-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
          range_start:
            "day-range-start [&>button]:rounded-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
          range_end:
            "day-range-end [&>button]:rounded-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
          range_middle:
            "[&>button]:rounded-none [&>button]:bg-transparent [&>button]:!text-accent-foreground [&>button]:hover:bg-transparent [&>button]:hover:!text-accent-foreground [&>button]:focus:!text-accent-foreground",
          outside:
            "day-outside [&>button]:text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        }}
        components={{Chevron}}
      />
      {hasFooter && (
        <div className="space-y-2 border-t p-3">
          {footerPresets?.length ? (
            <div className="flex flex-wrap gap-2">
              {footerPresets.map((preset) => (
                <NewButton
                  key={preset}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => handlePresetSelect(preset)}
                  text={presetConfig[preset].label}
                />
              ))}
            </div>
          ) : null}
          {customPresets?.length ? (
            <div className="flex flex-wrap gap-2">
              {customPresets.map((preset) => {
                const isActive = sameRange(value, preset.range);
                return (
                  <button
                    type="button"
                    key={preset.id}
                    onClick={() => handleCustomPresetSelect(preset)}
                    className={cn(
                      "rounded border px-2 py-0.5 text-[11px] font-medium transition-all",
                      isActive
                        ? "border-gray-300 bg-gray-100 text-gray-700"
                        : "border-gray-200 text-gray-500 hover:bg-gray-50",
                    )}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          ) : null}
          {showClearSelection && value ? (
            <NewButton
              type="button"
              variant="ghost"
              size="xs"
              onClick={handleClearSelection}
              className="w-full justify-center text-center"
            >
              Clear selection
            </NewButton>
          ) : null}
        </div>
      )}
    </div>
  );
};

export type DateRangePickerProps = {
  initialValue?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  disabledDates?: Matcher | Matcher[];
  footerPresets?: DateRangePickerPreset[];
  showTopControls?: boolean;
  triggerClassName?: string;
};

export const DateRangePicker = ({
  initialValue,
  onChange,
  disabledDates,
  footerPresets,
  showTopControls = false,
  triggerClassName,
}: DateRangePickerProps) => {
  const locale = useMatterLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<DateRange>(() => getResolvedRange(initialValue, disabledDates));
  const [draftValue, setDraftValue] = useState<DateRange>(() =>
    getResolvedRange(initialValue, disabledDates),
  );
  const dialogContainer = useDialogContainer();
  const initialValueKey = `${initialValue?.from?.getTime() ?? ""}:${initialValue?.to?.getTime() ?? ""}`;
  const previousInitialValueKey = useRef(initialValueKey);

  useEffect(() => {
    const initialValueChanged = previousInitialValueKey.current !== initialValueKey;
    previousInitialValueKey.current = initialValueKey;

    if (isOpen && !initialValueChanged) {
      return;
    }

    const nextValue = getResolvedRange(initialValue, disabledDates);

    setValue((currentValue) => sameRange(currentValue, nextValue) ? currentValue : nextValue);
    setDraftValue((currentValue) => sameRange(currentValue, nextValue) ? currentValue : nextValue);
  }, [disabledDates, initialValue, initialValueKey, isOpen]);

  const closePopover = () => {
    setDraftValue(value);
    setIsOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftValue(value);
      setIsOpen(true);
      return;
    }

    closePopover();
  };

  const handleDayClick = (day: Date, modifiers: Modifiers) => {
    if (modifiers.disabled) {
      return;
    }

    if (!draftValue.from || isCompleteRange(draftValue)) {
      setDraftValue({from: day, to: undefined});
      return;
    }

    const nextValue =
      day.getTime() < draftValue.from.getTime()
        ? {from: day, to: draftValue.from}
        : {from: draftValue.from, to: day};

    setDraftValue(nextValue);
    setValue(nextValue);
    onChange?.(nextValue);
  };

  const handlePresetSelect = (preset: DateRangePickerPreset) => {
    const nextValue = getClampedPresetRange(preset, disabledDates);

    if (!nextValue) {
      return;
    }

    setDraftValue(nextValue);
    setValue(nextValue);
    onChange?.(nextValue);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <NewButton
          type="button"
          variant="outline"
          size="md"
          className={triggerClassName}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-gray-500"/>
          <span className="min-w-0 truncate">{formatRange(value, locale)}</span>
        </NewButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto min-w-max p-0"
        container={dialogContainer ?? undefined}
      >
        <DateRangeCalendar
          value={draftValue}
          onDayClick={handleDayClick}
          disabledDates={disabledDates}
          footerPresets={footerPresets}
          showTopControls={showTopControls}
          onPresetSelect={handlePresetSelect}
        />
      </PopoverContent>
    </Popover>
  );
};
